import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ExternalLink,
  Settings,
  Download,
  User,
  Activity,
  MessageSquare,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  MoreVertical,
  Bot
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { getTenants, impersonateTenant, getTenantStats, getTenantTraffic, getTenantEvents } from '../../../api/backoffice';
import { VirtualizationTab } from './VirtualizationTab';
import { WhatsAppAccountSection } from '../WhatsAppAccountSection';

const VisitorTrendChart = ({ data }: { data: any[] }) => {
  return (
    <div className="h-[250px] w-full mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" />
          <XAxis
            dataKey="time"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#94a3b8', fontSize: 12 }}
            interval={4}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#94a3b8', fontSize: 12 }}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f1f5f9' }}
            itemStyle={{ color: '#3b82f6' }}
            labelStyle={{ color: '#94a3b8' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#3b82f6"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorValue)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const SessionDonutChart = () => {
  // Purely UI placeholder
  return (
    <div className="relative h-[200px] w-[200px] mx-auto flex items-center justify-center">
      <svg viewBox="0 0 100 100" className="transform -rotate-90 w-full h-full">
        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#3b82f6" strokeWidth="10" strokeDasharray="251.2" strokeDashoffset="60" />
        <circle cx="50" cy="50" r="30" fill="#1e293b" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-3xl font-bold text-white">Live</span>
      </div>
    </div>
  );
};

export const CustomerTelemetryPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [traffic, setTraffic] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'telemetry' | 'virtualization'>('telemetry');

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [tenants, s, t, e] = await Promise.all([
          getTenants(),
          getTenantStats(id),
          getTenantTraffic(id),
          getTenantEvents(id)
        ]);

        const found = tenants.find((t: any) => t.id === id);
        setTenant(found);
        setStats(s);
        setTraffic(t);
        setEvents(e);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleImpersonate = async () => {
    if (!tenant) return;
    try {
      await impersonateTenant(tenant.id);
      window.location.href = '/';
    } catch (err) {
      alert('Failed to impersonate tenant');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full text-text-muted">
      <Activity className="animate-spin mr-2" /> {t('backoffice.telemetry.loading')}
    </div>
  );
  if (!tenant) return <div className="p-8 text-red-400 text-center font-bold">{t('backoffice.telemetry.tenantNotFound')}</div>;

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <button
            data-testid="back-to-customers"
            onClick={() => navigate('/backoffice/customers')}
            className="flex items-center text-text-muted hover:text-white mb-2 text-sm transition-colors"
          >
            <ArrowLeft size={16} className="mr-1" /> {t('backoffice.telemetry.backToCustomers')}
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-white">{tenant.name}</h1>
            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${tenant.isLive
                ? 'bg-green-900/30 text-green-400 border-green-800/50'
                : 'bg-gray-800 text-text-muted border-gray-700'
              }`}>
              {tenant.isLive ? t('backoffice.telemetry.online') : t('backoffice.telemetry.offline')}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-text-muted">
            <span className="flex items-center gap-1">
              <ExternalLink size={12} /> {tenant.website || t('backoffice.customers.noWebsite')}
            </span>
            <span className="flex items-center gap-1 text-green-400">
              <div className="w-2 h-2 rounded-full bg-green-500"></div> {t('backoffice.telemetry.systemHealth')}: {stats?.systemHealth || '100%'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleImpersonate}
            className="flex items-center gap-2 px-4 py-2 bg-[#1e293b] hover:bg-[#2a3848] text-gray-200 rounded-lg border border-[#334155] transition-colors text-sm font-medium"
          >
            <User size={16} /> {t('backoffice.customers.impersonate')}
          </button>
          {/* H-13 — the "Export Data" button was REMOVED, not disabled. It had no onClick and there
              is no export endpoint anywhere in the server, so it was a primary-styled CTA beside a
              working one (Impersonate) that did nothing at all. A greyed-out control would say "you
              can't do this right now", which invites hunting for the permission that unlocks it;
              absent says the capability does not exist, which is the truth. It comes back with the
              feature, not before. */}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-[#334155]">
        <button
          onClick={() => setActiveTab('telemetry')}
          className={`px-6 py-3 text-sm font-bold tracking-wider uppercase transition-all border-b-2 ${activeTab === 'telemetry'
              ? 'text-blue-400 border-blue-500 bg-primary/5'
              : 'text-text-muted border-transparent hover:text-gray-300'
            }`}
        >
          <div className="flex items-center gap-2">
            <Activity size={16} /> Live Telemetry
          </div>
        </button>
        <button
          onClick={() => setActiveTab('virtualization')}
          className={`px-6 py-3 text-sm font-bold tracking-wider uppercase transition-all border-b-2 ${activeTab === 'virtualization'
              ? 'text-blue-400 border-blue-500 bg-primary/5'
              : 'text-text-muted border-transparent hover:text-gray-300'
            }`}
        >
          <div className="flex items-center gap-2">
            <Bot size={16} /> Agent Virtualization
          </div>
        </button>
      </div>

      {activeTab === 'telemetry' ? (
        <>
          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Visitors Chart (Span 2) */}
            <div className="lg:col-span-2 bg-[#1e293b] rounded-xl border border-[#334155] p-6 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('backoffice.telemetry.concurrentVisitors')}</h3>
                  <p className="text-sm text-text-muted">{t('backoffice.telemetry.liveTraffic')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex h-3 w-3 relative">
                    {tenant.isLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>}
                    <span className={`relative inline-flex rounded-full h-3 w-3 ${tenant.isLive ? 'bg-primary' : 'bg-gray-600'}`}></span>
                  </span>
                  <span className="text-sm font-medium text-gray-300">{t('backoffice.telemetry.liveNow')}: {tenant.visitorCount}</span>
                </div>
              </div>
              <VisitorTrendChart data={traffic} />
            </div>
            {/* Metric Overview */}
            <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-6 shadow-sm flex flex-col">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white">{t('backoffice.telemetry.engagement')}</h3>
                <p className="text-sm text-text-muted">{t('backoffice.telemetry.realTimeLoad')}</p>
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <SessionDonutChart />
                <div className="mt-8 space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      <span className="text-gray-300">{t('backoffice.telemetry.monitoredVisitors')}</span>
                    </div>
                    <span className="font-medium text-white">{tenant.visitorCount}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      <span className="text-gray-300">{t('backoffice.telemetry.activeAgents')}</span>
                    </div>
                    <span className="font-medium text-white">{tenant.agentCount}</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Counter Cards */}
            <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-6 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-text-muted">{t('backoffice.telemetry.totalLeads')}</h4>
                <TrendingUp size={16} className="text-green-500" />
              </div>
              <div className="text-3xl font-bold text-white mb-4">{stats?.leadsCount || 0}</div>
              <div className="text-xs text-text-muted">{t('backoffice.telemetry.savedInCrm')}</div>
            </div>

            <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-6 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-text-muted">{t('backoffice.telemetry.avgResponse')}</h4>
                <Clock size={16} className="text-purple-500" />
              </div>
              <div className="text-3xl font-bold text-white mb-4">{stats?.avgResponseTime || '00:00'}</div>
              <div className="text-xs text-text-muted">{t('backoffice.telemetry.firstResponseTime')}</div>
            </div>

            <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-6 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-text-muted">{t('backoffice.telemetry.chatsMonthly')}</h4>
                <MessageSquare size={16} className="text-pink-500" />
              </div>
              <div className="text-3xl font-bold text-white mb-4">{stats?.chatsThisMonth || 0}</div>
              <div className="text-xs text-text-muted">{t('backoffice.telemetry.lifecycleEvents')}</div>
            </div>
          </div>

          {/* Recent System Events */}
          <div className="bg-[#1e293b] rounded-xl border border-[#334155] overflow-hidden shadow-sm">
            <div className="p-6 border-b border-[#334155] flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">{t('backoffice.telemetry.recentEvents')}</h3>
              <button className="text-sm text-blue-400 hover:text-blue-300 font-medium">{t('backoffice.telemetry.viewAllLogs')}</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-nav text-text-muted font-medium">
                  <tr>
                    <th className="px-6 py-3">{t('backoffice.telemetry.timestamp')}</th>
                    <th className="px-6 py-3">{t('backoffice.telemetry.eventType')}</th>
                    <th className="px-6 py-3">{t('backoffice.telemetry.status')}</th>
                    <th className="px-6 py-3">{t('backoffice.telemetry.details')}</th>
                    <th className="px-6 py-3 text-right">{t('backoffice.telemetry.action')}</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-[#334155]">
                  {events.map((event) => (
                    <tr key={event.id} className="hover:bg-[#2a3848] transition-colors">
                      <td className="px-6 py-4 text-gray-300 font-mono text-xs">
                        {new Date(event.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-white font-medium">{event.type}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${event.status === 'Failed' ? 'bg-red-900/30 text-red-400 border-red-800/50' :
                            event.status === 'Warning' ? 'bg-yellow-900/30 text-yellow-400 border-yellow-800/50' :
                              'bg-green-900/30 text-green-400 border-green-800/50'
                          }`}>
                          {event.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-text-muted max-w-xs truncate" title={event.details}>{event.details}</td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-text-muted hover:text-white transition-colors">
                          {event.action === 'retry' ? <RefreshCw size={16} /> :
                            event.action === 'view' ? <Eye size={16} /> :
                              <Download size={16} />
                          }
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-account WhatsApp connection (AC-W1) — scoped to this account's id, shows + edits its
              own WhatsAppCredential. The platform-global app-config stays on the central page (AC-W8). */}
          <WhatsAppAccountSection accountId={id!} />
        </>
      ) : (
        <VirtualizationTab tenantId={id!} readOnly={true} />
      )}
    </div>
  );
};
